import { EditorThemeClasses, TextFormatType } from 'lexical'
import { MergeFieldSetting, MergeTagType } from '@web/graphql-types'
import Decimal from 'decimal.js'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore We don't have the types for this package
import { evaluate } from 'decimal.js-extensions-evaluate'

evaluate.extend(Decimal)

export const countUpTo = (elem: Element, endVal: number, duration: number, startVal = 0) => {
  let start = startVal
  const increment = endVal / (duration * 60) // Assuming ~60 frames per second for smooth animation

  const step = () => {
    start += increment
    if (start >= endVal) {
      elem.textContent = endVal.toLocaleString('en') // Ensure it ends on the exact endVal
      return
    }
    elem.textContent = Math.floor(start).toLocaleString('en')
    requestAnimationFrame(step)
  }

  requestAnimationFrame(step)
}

export type RoundingOption = 'round-down' | 'round-up' | 'round-nearest'

export type NumberFormat = '0000' | '0,000' | '0.00' | '0,000.00'

const getDecimalPlaces = (format?: NumberFormat) => {
  switch (format) {
    case '0.00':
    case '0,000.00':
      return 2
    case '0,000':
    case '0000':
    default:
      return 0
  }
}

const applyRounding = (value: Decimal, decimalPlaces: number, rounding?: RoundingOption) => {
  const factor = new Decimal(10).pow(decimalPlaces)
  const shifted = value.mul(factor)

  switch (rounding) {
    case 'round-up':
      return shifted.ceil().div(factor)

    case 'round-nearest':
      return shifted.round().div(factor)

    case 'round-down':
    default:
      return shifted.floor().div(factor)
  }
}

const formatNumber = (value: Decimal, format?: NumberFormat) => {
  switch (format) {
    case '0.00':
      return value.toNumber().toLocaleString('en-AU', {
        useGrouping: false,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    case '0,000.00':
      return value.toNumber().toLocaleString('en-AU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    case '0000':
      return value.toFixed(0)
    case '0,000':
    default:
      return value.toNumber().toLocaleString('en-AU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
  }
}

export const evaluateFormula = (
  formula: string,
  tags: Record<string, string>,
  options?: { rounding?: RoundingOption; format?: NumberFormat },
): string => {
  try {
    const actualFormula = formula.replace(/\[(.*?)\]/g, (_, tag) => {
      const value = tags[tag]?.replace(/[$,]/g, '') || '0'
      return value
    })

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore There's no types for this but we extend it as above
    let result = Decimal.eval(actualFormula)

    const decimalPlaces = getDecimalPlaces(options?.format)
    result = applyRounding(result, decimalPlaces, options?.rounding)

    return formatNumber(result, options?.format)
  } catch {
    return 'NaN'
  }
}

const BASE_MERGE_FIELD = {
  QueryParameter: '',
  CsvHeader: '',
  TemplateName: '',
}

export const MATCHED_GIVING_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Goal',
    MergeFieldSettingId: 'MatchedGiving.Goal',
    MergeTag: '{{MatchedGiving.Goal}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: '% Of Total Goal Raised',
    MergeFieldSettingId: 'MatchedGiving.PercentageOfGoalRaised',
    MergeTag: '{{MatchedGiving.PercentageOfGoalRaised}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Amount Raised',
    MergeFieldSettingId: 'MatchedGiving.AmountRaised',
    MergeTag: '{{MatchedGiving.AmountRaised}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
]

export const COMMENTS_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Total Donors',
    MergeFieldSettingId: 'Comments.TotalDonors',
    MergeTag: '{{Comments.TotalDonors}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
]

export const PROGRESS_BAR_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Goal',
    MergeFieldSettingId: 'ProgressBar.Goal',
    MergeTag: '{{ProgressBar.Goal}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: '% Of Total Goal Raised',
    MergeFieldSettingId: 'ProgressBar.PercentageOfGoalRaised',
    MergeTag: '{{ProgressBar.PercentageOfGoalRaised}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Amount Raised',
    MergeFieldSettingId: 'ProgressBar.AmountRaised',
    MergeTag: '{{ProgressBar.AmountRaised}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
]

export const DONOR_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'First Name',
    MergeFieldSettingId: 'First Name',
    MergeTag: '{{Donor.FirstName}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Last Name',
    MergeFieldSettingId: 'Last Name',
    MergeTag: '{{Donor.LastName}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Email',
    MergeFieldSettingId: 'Email',
    MergeTag: '{{Donor.Email}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Organisation',
    MergeFieldSettingId: 'Organisation',
    MergeTag: '{{Donor.Organisation}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Mobile',
    MergeFieldSettingId: 'Mobile',
    MergeTag: '{{Donor.Mobile}}',
  },
]

export const GIVING_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Giving ID',
    MergeFieldSettingId: 'GivingId',
    MergeTag: '{{Giving.Id}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Amount',
    MergeFieldSettingId: 'Amount',
    MergeTag: '{{Giving.Amount}}',
    MergeTagFallback: '0',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Date Donated',
    MergeFieldSettingId: 'DateDonated',
    MergeTag: '{{Giving.Date}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Giving Frequency',
    MergeFieldSettingId: 'Frequency',
    MergeTag: '{{Giving.Frequency}}',
  },
]

export const CHARITY_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Charity Name',
    MergeFieldSettingId: 'CharityName',
    MergeTag: '{{Charity.Name}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Charity ABN',
    MergeFieldSettingId: 'CharityABN',
    MergeTag: '{{Charity.ABN}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Charity Phone',
    MergeFieldSettingId: 'CharityPhone',
    MergeTag: '{{Charity.Phone}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Charity Email',
    MergeFieldSettingId: 'CharityEmail',
    MergeTag: '{{Charity.Email}}',
  },
]

export const CAMPAIGN_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Name',
    MergeFieldSettingId: 'CampaignName',
    MergeTag: '{{Campaign.Name}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Amount Raised',
    MergeFieldSettingId: 'Campaign.AmountRaised',
    MergeTag: '{{Campaign.AmountRaised}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
]

export const PAGE_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Amount Raised',
    MergeFieldSettingId: 'Page.AmountRaised',
    MergeTag: '{{Page.AmountRaised}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
]

export const COUNTDOWN_TIMER_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Days Left',
    MergeFieldSettingId: 'CountdownTimer.DaysLeft',
    MergeTag: '{{Countdown.DaysLeft}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Hours Left',
    MergeFieldSettingId: 'CountdownTimer.HoursLeft',
    MergeTag: '{{Countdown.HoursLeft}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Minutes Left',
    MergeFieldSettingId: 'CountdownTimer.MinutesLeft',
    MergeTag: '{{Countdown.MinutesLeft}}',
    MergeTagFallback: '0',
    MergeTagType: MergeTagType.Dynamic,
  },
]

export const PROFILE_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Profile Name',
    MergeFieldSettingId: 'Profile.Name',
    MergeTag: '{{Profile.Name}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Profile Bio',
    MergeFieldSettingId: 'Profile.Bio',
    MergeTag: '{{Profile.Bio}}',
  },
]

export const PROFILE_EXPANDED_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'First Name',
    MergeFieldSettingId: 'Profile.FirstName',
    MergeTag: '{{Profile.FirstName}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Last Name',
    MergeFieldSettingId: 'Profile.LastName',
    MergeTag: '{{Profile.LastName}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Full Name',
    MergeFieldSettingId: 'Profile.FullName',
    MergeTag: '{{Profile.FullName}}',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Url',
    MergeFieldSettingId: 'Profile.ProfileUrl',
    MergeTag: '{{Profile.ProfileUrl}}',
    MergeTagType: MergeTagType.Link,
    MergeTagDisplayText: 'View your fundraiser.',
  },
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Goal',
    MergeFieldSettingId: 'Profile.Goal',
    MergeTag: '{{Profile.Goal}}',
    MergeTagFallback: '0',
  },
]

export const CUSTOM_MERGE_TAGS: MergeFieldSetting[] = [
  {
    ...BASE_MERGE_FIELD,
    MergeFieldName: 'Formula',
    MergeFieldSettingId: 'Custom.Formula',
    MergeTag: '{{Custom.Formula}}',
    MergeTagType: MergeTagType.Formula,
    MergeTagFallback: '0',
  },
]
// TAKEN FROM THE LEXICAL REPO

export const IS_BOLD = 1
export const IS_ITALIC = 1 << 1
export const IS_STRIKETHROUGH = 1 << 2
export const IS_UNDERLINE = 1 << 3
export const IS_CODE = 1 << 4
export const IS_SUBSCRIPT = 1 << 5
export const IS_SUPERSCRIPT = 1 << 6
export const IS_HIGHLIGHT = 1 << 7

export const TEXT_TYPE_TO_FORMAT: Record<TextFormatType | string, number> = {
  bold: IS_BOLD,
  code: IS_CODE,
  highlight: IS_HIGHLIGHT,
  italic: IS_ITALIC,
  strikethrough: IS_STRIKETHROUGH,
  subscript: IS_SUBSCRIPT,
  superscript: IS_SUPERSCRIPT,
  underline: IS_UNDERLINE,
}

export function getElementInnerTag(format: number): string {
  if (format & IS_BOLD) {
    return 'strong'
  }
  if (format & IS_ITALIC) {
    return 'em'
  }
  return 'span'
}

function normalizeClassNames(...classNames: Array<typeof undefined | boolean | null | string>): Array<string> {
  const rval: string[] = []
  for (const className of classNames) {
    if (className && typeof className === 'string') {
      for (const [s] of className.matchAll(/\S+/g)) {
        rval.push(s)
      }
    }
  }
  return rval
}

function getCachedClassNameArray(classNamesTheme: EditorThemeClasses, classNameThemeType: string): Array<string> {
  if (classNamesTheme.__lexicalClassNameCache === undefined) {
    classNamesTheme.__lexicalClassNameCache = {}
  }
  const classNamesCache = classNamesTheme.__lexicalClassNameCache
  const cachedClassNames = classNamesCache[classNameThemeType]
  if (cachedClassNames !== undefined) {
    return cachedClassNames
  }
  const classNames = classNamesTheme[classNameThemeType]
  // As we're using classList, we need
  // to handle className tokens that have spaces.
  // The easiest way to do this to convert the
  // className tokens to an array that can be
  // applied to classList.add()/remove().
  if (typeof classNames === 'string') {
    const classNamesArr = normalizeClassNames(classNames)
    classNamesCache[classNameThemeType] = classNamesArr
    return classNamesArr
  }
  return classNames
}

export function getTextThemeClassNames(
  prevFormat: number,
  nextFormat: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  textClassNames: any,
): string[] {
  let domClassList: string[] = []
  // Firstly we handle the base theme.
  let classNames = getCachedClassNameArray(textClassNames, 'base')
  if (classNames !== undefined) {
    domClassList.push(...classNames)
  }
  // Secondly we handle the special case: underline + strikethrough.
  // We have to do this as we need a way to compose the fact that
  // the same CSS property will need to be used: text-decoration.
  // In an ideal world we shouldn't have to do this, but there's no
  // easy workaround for many atomic CSS systems today.
  classNames = getCachedClassNameArray(textClassNames, 'underlineStrikethrough')
  let hasUnderlineStrikethrough = false
  const prevUnderlineStrikethrough = prevFormat & IS_UNDERLINE && prevFormat & IS_STRIKETHROUGH
  const nextUnderlineStrikethrough = nextFormat & IS_UNDERLINE && nextFormat & IS_STRIKETHROUGH

  if (classNames !== undefined) {
    if (nextUnderlineStrikethrough) {
      hasUnderlineStrikethrough = true
      if (!prevUnderlineStrikethrough) {
        domClassList.push(...classNames)
      }
    } else if (prevUnderlineStrikethrough) {
      domClassList = domClassList.filter((x) => classNames.includes(x))
    }
  }

  for (const key in TEXT_TYPE_TO_FORMAT) {
    const format = key
    const flag = TEXT_TYPE_TO_FORMAT[format]
    classNames = getCachedClassNameArray(textClassNames, key)
    if (classNames !== undefined) {
      if (nextFormat & flag) {
        if (hasUnderlineStrikethrough && (key === 'underline' || key === 'strikethrough')) {
          if (prevFormat & flag) {
            domClassList = domClassList.filter((x) => classNames.includes(x))
          }
          continue
        }
        if (
          (prevFormat & flag) === 0 ||
          (prevUnderlineStrikethrough && key === 'underline') ||
          key === 'strikethrough'
        ) {
          domClassList.push(...classNames)
        }
      } else if (prevFormat & flag) {
        domClassList = domClassList.filter((x) => classNames.includes(x))
      }
    }
  }

  return domClassList
}
